import { BranchContext } from './types';
import { PlanError, request } from './aiPlansApi';
export interface TextIdentity { subject: string; system: string; context: BranchContext; contractId: string; }
export interface TextRequestIds { requestId: string; callId: string; }
export interface TextUsage { grantedMicros: number; confirmedMicros: number; reservedMicros: number; pendingMicros: number; availableMicros: number; }
export interface TextExecution {
 call: TextRequestIds & { state: 'reserved' | 'dispatched' | 'uncertain' | 'settled' | 'released'; actual: { brlMicros: number } | null; estimate: { brlMicros: number } };
 result: { outcome: 'completed' | 'empty' | 'refused' | 'incomplete' | 'failed' | 'uncertain'; text: string | null } | null;
}
const uuidPattern = /^[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/;
export const validTextIds = (value: any): value is TextRequestIds => Boolean(value && typeof value.requestId === 'string' && typeof value.callId === 'string' && uuidPattern.test(value.requestId) && uuidPattern.test(value.callId));
const amount = (n: any) => Number.isSafeInteger(n) && n >= 0;
const contextBody = (identity: TextIdentity) => ({ companyId: identity.context.companyId, branchId: identity.context.branchId });
function assertScope(value: any, identity: TextIdentity): void {
 if (!value || String(value.companyId) !== identity.context.companyId || String(value.branchId) !== identity.context.branchId || value.contractId !== identity.contractId
  || !Number.isSafeInteger(value.bankId) || value.bankId < 1 || typeof value.tenant !== 'string' || !value.tenant) {
  throw new PlanError(502, 'A resposta não corresponde à filial e ao contrato selecionados.');
 }
}
function execution(value: any, identity: TextIdentity, ids: TextRequestIds): TextExecution {
 assertScope(value?.scope, identity);
 const call = value?.call; const result = value?.result;
 if (!call || call.callId !== ids.callId || call.requestId !== ids.requestId || call.subject !== identity.subject || call.systemId !== identity.system
  || !['reserved', 'dispatched', 'uncertain', 'settled', 'released'].includes(call.state) || !amount(call.estimate?.brlMicros)
  || (call.actual !== null && !amount(call.actual?.brlMicros)) || (call.state === 'settled' && call.actual === null)
  || result === undefined || (result !== null && (!['completed', 'empty', 'refused', 'incomplete', 'failed', 'uncertain'].includes(result.outcome)
   || (result.outcome === 'completed' ? typeof result.text !== 'string' || call.state !== 'settled' : result.text !== null)))) {
  throw new PlanError(502, 'Não foi possível conferir o resultado. Consulte a mesma solicitação.');
 }
 return { call, result };
}
export async function executeText(token: string, identity: TextIdentity, ids: TextRequestIds, input: string): Promise<TextExecution> {
 if (!validTextIds(ids)) throw new PlanError(422, 'Identificador da solicitação inválido.');
 return execution(await request('/v1/me/ai-plan-executions?system=' + encodeURIComponent(identity.system), token, 'POST', { context: contextBody(identity), ...ids, input }), identity, ids);
}
export async function recoverText(token: string, identity: TextIdentity, ids: TextRequestIds): Promise<TextExecution> {
 if (!validTextIds(ids)) throw new PlanError(422, 'Identificador da solicitação inválido.');
 return execution(await request('/v1/me/ai-plan-executions/recover?system=' + encodeURIComponent(identity.system), token, 'POST', { context: contextBody(identity), callId: ids.callId }), identity, ids);
}
export async function cancelText(token: string, identity: TextIdentity, ids: TextRequestIds): Promise<void> {
 if (!validTextIds(ids)) throw new PlanError(422, 'Identificador da solicitação inválido.');
 const value = await request('/v1/me/ai-plan-executions/cancel?system=' + encodeURIComponent(identity.system), token, 'POST', { context: contextBody(identity), ...ids });
 assertScope(value?.scope, identity);
 if (value?.requestId !== ids.requestId || value?.callId !== ids.callId || value?.state !== 'cancelled') {
  throw new PlanError(502, 'Não foi possível confirmar o encerramento. Consulte a mesma solicitação.');
 }
}
export async function getTextAvailability(token: string, identity: TextIdentity, signal?: AbortSignal): Promise<boolean> {
 const value = await request('/v1/me/ai-plan-executions/availability?system=' + encodeURIComponent(identity.system), token, 'POST', { context: contextBody(identity) }, signal);
 assertScope(value?.scope, identity);
 if (typeof value?.generationEnabled !== 'boolean') throw new PlanError(502, 'Não foi possível conferir a disponibilidade da geração.');
 return value.generationEnabled;
}
export async function getTextUsage(token: string, identity: TextIdentity, signal?: AbortSignal): Promise<TextUsage> {
 const value = await request('/v1/me/ai-plan-usage?system=' + encodeURIComponent(identity.system), token, 'POST', { context: contextBody(identity) }, signal);
 assertScope(value?.scope, identity); const usage = value?.usage;
 if (!usage || !['grantedMicros', 'confirmedMicros', 'reservedMicros', 'pendingMicros', 'availableMicros'].every(field => amount(usage[field]))
  || usage.availableMicros !== Math.max(0, usage.grantedMicros - usage.confirmedMicros - usage.reservedMicros - usage.pendingMicros)) throw new PlanError(502, 'Saldo inválido. Atualize antes de enviar.');
 return usage;
}
export function newTextRequest(): TextRequestIds {
 const uuid = () => {
  const bytes = new Uint8Array(16); window.crypto.getRandomValues(bytes); bytes[6] = (bytes[6] & 15) | 64; bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, value => value.toString(16).padStart(2, '0')).join('');
  return [hex.slice(0, 8), hex.slice(8, 12), hex.slice(12, 16), hex.slice(16, 20), hex.slice(20)].join('-');
 };
 return { requestId: uuid(), callId: uuid() };
}
export const textStorageKey = (identity: TextIdentity) => 'okton.ai.text.v1:' + JSON.stringify([identity.subject, identity.system, identity.context.companyId, identity.context.branchId, identity.contractId]);
export function loadTextRequest(key: string): TextRequestIds | null {
 const raw = window.sessionStorage.getItem(key); if (raw === null) return null;
 const value = JSON.parse(raw);
 if (!validTextIds(value) || Object.keys(value).length !== 2) throw new Error('Não foi possível conferir a solicitação salva neste navegador.');
 return { requestId: value.requestId, callId: value.callId };
}
export function saveTextRequest(key: string, ids: TextRequestIds): void {
 if (!validTextIds(ids)) throw new Error('Identificador da solicitação inválido.');
 try {
  window.sessionStorage.setItem(key, JSON.stringify(ids));
  if (window.sessionStorage.getItem(key) !== JSON.stringify(ids)) throw new Error();
 } catch { throw new Error('Não foi possível guardar a solicitação neste navegador. Nenhuma geração foi enviada.'); }
}
export const textBytes = (value: string) => new Blob([value]).size;
export const textMoney = (micros: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(micros / 1000000);

export interface TextHistoryItem extends TextRequestIds {
 systemId: string; subject: string;
 scope: { bankId: number; tenant: string; companyId: number; branchId: number; contractId: string };
 state: TextExecution['call']['state']; reservedMicros: number; actualMicros: number | null; createdAt: string; updatedAt: string;
}
export interface TextHistoryPage { items: TextHistoryItem[]; nextOffset: number | null; }
export const textStatuses: Record<TextExecution['call']['state'], string> = {
 reserved: 'Aguardando confirmação do envio', dispatched: 'Aguardando resposta', uncertain: 'Aguardando conferência da Okton',
 settled: 'Solicitação concluída', released: 'Solicitação encerrada',
};
export function validHistoryItem(item: any): item is TextHistoryItem {
 return Boolean(item) && validTextIds({ requestId: item.requestId, callId: item.callId }) && typeof item.systemId === 'string' && typeof item.subject === 'string'
  && !!item.scope && Number.isSafeInteger(item.scope.bankId) && item.scope.bankId > 0
  && Number.isSafeInteger(item.scope.companyId) && item.scope.companyId > 0
  && Number.isSafeInteger(item.scope.branchId) && item.scope.branchId > 0
  && typeof item.scope.tenant === 'string' && /^[a-f0-9]{32}$/.test(item.scope.contractId)
  && Object.prototype.hasOwnProperty.call(textStatuses, item.state) && amount(item.reservedMicros)
  && (item.actualMicros === null || amount(item.actualMicros))
  && typeof item.createdAt === 'string' && Number.isFinite(Date.parse(item.createdAt))
  && typeof item.updatedAt === 'string' && Number.isFinite(Date.parse(item.updatedAt));
}
export function validHistoryPage(value: any): value is TextHistoryPage {
 return !!value && Array.isArray(value.items) && value.items.length <= 50 && value.items.every(validHistoryItem)
  && (value.nextOffset === null || (Number.isSafeInteger(value.nextOffset) && value.nextOffset > 0 && value.nextOffset <= 100050));
}
export async function getTextHistory(token: string, identity: TextIdentity, offset = 0, signal?: AbortSignal): Promise<TextHistoryPage> {
 const value = await request('/v1/me/ai-plan-executions/history?system=' + encodeURIComponent(identity.system), token, 'POST', { context: contextBody(identity), offset }, signal);
 assertScope(value?.scope, identity);
 if (!validHistoryPage(value)) throw new PlanError(502, 'Histórico inválido. Atualize para consultar.');
 value.items.forEach(item => {
  assertScope(item.scope, identity);
  if (item.subject !== identity.subject || item.systemId !== identity.system) throw new PlanError(502, 'O histórico não corresponde ao usuário e sistema selecionados.');
 });
 return value;
}
